import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { Component, computed, inject, input, output } from "@angular/core";
import { RouterLink } from "@angular/router";

import { Maybe } from "../../../../../apis/networkdashboard/graphql/graphql";
import { CimImpactTypePipe } from "../../../../../helpers/cim-pipe/cim-pipe";
import { isSupportedProductTag } from "../../../../../helpers/subscription/supported-product-tags";
import { AuthService } from "../../../../../services/authservice";
import { ServiceIcon } from "../../../../subscription-detail/service-icon/service-icon";
import { ServiceId } from "../../../../subscription-detail/service-id/service-id";

export interface AffectedServiceViewModel {
  product?: Maybe<{
    type: string;
    tag: string;
  }>;
  customerId: string;
  customerDescription: string;
  customerName?: string;
  subscriptionId: string;
}

@Component({
  selector: "affected-service",
  templateUrl: "./affected-service.html",
  styleUrls: ["./affected-service.scss"],
  imports: [AngularSvgIconModule, TranslatePipe, ServiceIcon, ServiceId, CimImpactTypePipe, RouterLink],
})
export class AffectedService {
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);

  protected service = input.required<AffectedServiceViewModel>();
  protected impact = input<string>("");
  protected linkState = input<Record<string, unknown> | undefined>(undefined);
  protected linkActivated = output<MouseEvent>();

  protected productType = computed(() => this.service()?.product?.type ?? "unknown");
  protected iconType = computed(() => this.productType().toLowerCase());
  protected description = computed(() => this.service().customerDescription);
  protected subscriptionId = computed(() => this.service().subscriptionId);

  protected productUrlParts = computed(() => {
    const product = this.service().product;
    if (!product) return [];
    return [product.type, product.tag];
  });
  protected routerLink = computed(() => ["/diensten", ...this.productUrlParts(), this.service().subscriptionId]);

  protected emitClick(event: MouseEvent): void {
    if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      this.linkActivated.emit(event);
    }
  }

  protected isAccessible = computed(() => {
    const service = this.service();
    const user = this.auth.viewingCustomer();

    const hasDetailPage = isSupportedProductTag(this.service().product?.tag);
    const sameCustomer = service.customerId === user.customerId;
    return hasDetailPage && sameCustomer;
  });
  protected displayName = computed(() => this.service().customerName || this.translate.instant("Service.Owner"));
}
